import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const StockDashboard = () => {
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingStockData, setLoadingStockData] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState({
    specificDate: "",
    startDate: "",
    endDate: "",
    monthYear: "",
    search: "",
    reserve_for_branch: "",
  });
  const branches = ["Lehulu", "Kalkidan", "Store"];

  useEffect(() => {
    if (selectedBranch) {
      fetchCategories();
    }
  }, [selectedBranch]);

  useEffect(() => {
    if (selectedCategory && selectedBranch) {
      fetchItems();
    }
  }, [selectedCategory, selectedBranch]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get(
        "https://backend.lehuluet.com/api/categories/categories"
      );
      setCategories(response.data.data);
    } catch (error) {
      toast.error("Error fetching categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchItems = async () => {
    setLoadingItems(true);
    setSelectedSubCategory(null);
    setShowFilters(false);
    try {
      const response = await axios.get(
        `https://backend.lehuluet.com/api/items/category/${selectedCategory}`,
        {
          params: { branch: selectedBranch },
        }
      );
      const fetchedItems =
        response.data.data && response.data.data.length > 0
          ? response.data.data
          : [];
      setItems(fetchedItems);

      // FIXED: Use cat.id instead of cat._id
      const category = categories.find((cat) => cat.id === parseInt(selectedCategory));
      if (category) {
        if (category.name === "Goma") {
          const sizes = [
            ...new Set(fetchedItems.map((item) => item.size)),
          ].filter((size) => size);
          setSubCategories(sizes);
        } else if (category.name === "Battery") {
          const amperes = [
            ...new Set(fetchedItems.map((item) => item.ampere)),
          ].filter((ampere) => ampere);
          setSubCategories(amperes);
        } else {
          setSubCategories([]);
        }
      }
    } catch (error) {
      toast.error("There is no item in this category.");
      setItems([]);
      setSubCategories([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const fetchStockData = async (itemId) => {
    setLoadingStockData(true);
    try {
      const response = await axios.get(
        `https://backend.lehuluet.com/api/items/stock-by-item/${itemId}`
      );
      setStockData(response.data.data);
    } catch (error) {
      toast.error("Error fetching stock data");
    } finally {
      setLoadingStockData(false);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    // FIXED: Use item.id instead of item._id
    fetchStockData(item.id);
  };

  const handleSubCategoryClick = (subCategory) => {
    setSelectedSubCategory(subCategory);
  };

  const getStockTimeline = () => {
    if (!stockData) return [];

    const timeline = [
      ...stockData.stockIns.map((item) => ({ ...item, type: "in" })),
      ...stockData.stockOuts.map((item) => ({ ...item, type: "out" })),
    ];

    return timeline.sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const applyFilters = (itemsToFilter) => {
    let filtered = [...itemsToFilter];

    // Normalize date to YYYY-MM-DD format for comparison
    const normalizeDate = (date) => {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
    };

    // Filter by specific date
    if (filter.specificDate) {
      const filterDate = normalizeDate(filter.specificDate);
      filtered = filtered.filter((item) => {
        const itemDate = normalizeDate(item.created_at);
        return itemDate === filterDate;
      });
    }

    // Filter by date range
    if (filter.startDate && filter.endDate) {
      const start = new Date(filter.startDate);
      const end = new Date(filter.endDate);
      // Set end date to end of day
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.created_at);
        return itemDate >= start && itemDate <= end;
      });
    }

    // Filter by month/year
    if (filter.monthYear) {
      const [year, month] = filter.monthYear.split("-");
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.created_at);
        return (
          itemDate.getFullYear() === parseInt(year) &&
          itemDate.getMonth() === parseInt(month) - 1
        );
      });
    }

    // Search by name, size, brand, uniquesize, ampere, position, origin
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.name && item.name.toLowerCase().includes(searchLower)) ||
          (item.size && item.size.toLowerCase().includes(searchLower)) ||
          (item.brand && item.brand.toLowerCase().includes(searchLower)) ||
          (item.uniquesize &&
            item.uniquesize.toLowerCase().includes(searchLower)) ||
          (item.ampere && item.ampere.toLowerCase().includes(searchLower)) ||
          (item.position &&
            item.position.toLowerCase().includes(searchLower)) ||
          (item.origin && item.origin.toLowerCase().includes(searchLower))
      );
    }

    // Filter by reserve_for_branch
    if (filter.reserve_for_branch) {
      filtered = filtered.filter(
        (item) => item.reserve_for_branch === filter.reserve_for_branch
      );
    }

    return filtered;
  };

  // FIXED: Use cat.id instead of cat._id
  const filteredItems = selectedSubCategory
    ? items.filter((item) => {
        const category = categories.find((cat) => cat.id === parseInt(selectedCategory));
        if (category && category.name === "Goma") {
          return item.size === selectedSubCategory;
        } else if (category && category.name === "Battery") {
          return item.ampere === selectedSubCategory;
        }
        return true;
      })
    : items;

  const displayedItems = applyFilters(filteredItems);

  const exportToCSV = () => {
    if (!stockData) return;

    const timeline = getStockTimeline();
    const headers = ["Date", "Type", "Quantity", "Unit Price", "Note", "User"];
    const rows = timeline.map((transaction) => [
      new Date(transaction.created_at).toLocaleDateString(),
      transaction.type === "in" ? "Stock In" : "Stock Out",
      transaction.quantity || "",
      transaction.unitprice || "",
      transaction.note || "",
      transaction.user?.name || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedItem.name}_stock_history.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportItemsToCSV = () => {
    if (!displayedItems.length) return;

    const headers = [
      "Name",
      "Brand",
      "Size",
      "saleprice",
      "Unique Size",
      "Ampere",
      "Position",
      "Origin",
      "Quantity",
      "Category",
    ];
    const rows = displayedItems.map((item) => [
      item.name || "",
      item.brand || "",
      item.size || "",
      item.saleprice || "",
      item.uniquesize || "",
      item.ampere || "",
      item.position || "",
      item.origin || "",
      item.quantity || "",
      item.category?.name || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    // FIXED: Use cat.id instead of cat._id
    link.download = `${selectedBranch}_${
      categories.find((cat) => cat.id === parseInt(selectedCategory))?.name || "items"
    }_items.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <h1 className="text-3xl  text-teal-700 font-bold mb-6 text-center">
        Stock Dashboard
      </h1>

      {/* Branch Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {branches.map((branch) => (
          <div
            key={branch}
            onClick={() => setSelectedBranch(branch)}
            className={`p-4 rounded-lg shadow-md cursor-pointer text-center transition-all duration-300 ${
              selectedBranch === branch
                ? "bg-teal-500 text-white scale-105"
                : "bg-white hover:bg-gray-50"
            }`}
          >
            <h2 className="text-xl font-semibold">{branch}</h2>
          </div>
        ))}
      </div>

      {/* Categories Section */}
      {selectedBranch && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <h2 className="text-2xl font-semibold mb-4">
            Categories in {selectedBranch}
          </h2>

          {loadingCategories ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <div
                  // FIXED: Use category.id instead of category._id
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                    selectedCategory === category.id
                      ? "border-teal-500 bg-teal-50"
                      : "border-gray-200 hover:border-teal-300"
                  }`}
                >
                  <h3 className="font-semibold">{category.name}</h3>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sub-Categories Section */}
      {selectedCategory && subCategories.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <h2 className="text-2xl font-semibold mb-4">
            {/* FIXED: Use cat.id instead of cat._id */}
            {categories.find((cat) => cat.id === parseInt(selectedCategory))?.name ===
            "Goma"
              ? "Sizes"
              : "Amperes"}{" "}
            in {categories.find((cat) => cat.id === parseInt(selectedCategory))?.name}
          </h2>

          {loadingItems ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {subCategories.map((subCategory) => (
                <div
                  key={subCategory}
                  onClick={() => handleSubCategoryClick(subCategory)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                    selectedSubCategory === subCategory
                      ? "border-teal-500 bg-teal-50"
                      : "border-gray-200 hover:border-teal-300"
                  }`}
                >
                  <h3 className="font-semibold">
                    {/* FIXED: Use cat.id instead of cat._id */}
                    {categories.find((cat) => cat.id === parseInt(selectedCategory))
                      ?.name === "Goma"
                      ? "Size"
                      : "Ampere"}
                    : {subCategory}
                  </h3>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Items Section with Filter Button */}
      {selectedCategory && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
            <h2 className="text-xl md:text-2xl font-semibold">
              Items in {selectedBranch}{" "}
              {selectedSubCategory
                ? `(${
                    // FIXED: Use cat.id instead of cat._id
                    categories.find((cat) => cat.id === parseInt(selectedCategory))
                      ?.name === "Goma"
                      ? "Size"
                      : "Ampere"
                  }: ${selectedSubCategory})`
                : ""}
            </h2>
            {items.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <button
                  onClick={toggleFilters}
                  className="bg-teal-500 text-white px-4 py-2 rounded hover:bg-teal-600 transition"
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </button>
                <button
                  onClick={exportItemsToCSV}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition"
                >
                  Export Items
                </button>
              </div>
            )}
          </div>

          {showFilters && (
            <div className="mb-6 p-4 border rounded bg-gray-50">
              <h3 className="text-xl font-semibold mb-4">Filter Items</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    label: "Specific Date",
                    name: "specificDate",
                    type: "date",
                  },
                  {
                    label: "Date Range Start",
                    name: "startDate",
                    type: "date",
                  },
                  { label: "Date Range End", name: "endDate", type: "date" },
                  { label: "Month/Year", name: "monthYear", type: "month" },
                  {
                    label: "Search",
                    name: "search",
                    type: "text",
                    placeholder: "Search by name, size, etc.",
                  },
                ].map((input) => (
                  <div key={input.name}>
                    <label className="block text-gray-700">{input.label}</label>
                    <input
                      type={input.type}
                      name={input.name}
                      value={filter[input.name]}
                      onChange={handleFilterChange}
                      placeholder={input.placeholder || ""}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-gray-700">
                    Reserve for Branch
                  </label>
                  <select
                    name="reserve_for_branch"
                    value={filter.reserve_for_branch}
                    onChange={handleFilterChange}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">All Branches</option>
                    <option value="Lehulu">Lehulu</option>
                    <option value="Kalkidan">Kalkidan</option>
                    <option value="Store">Store</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {loadingItems ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
            </div>
          ) : displayedItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      "Brand",
                      "Size",
                       "saleprice",
                      "Unique Size",
                     
                      "Ampere",
                      "Position",
                      "Origin",
                      "Quantity",
                      "Category",
                      "Reserve for Branch",
                    ].map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedItems.map((item) => (
                    <tr
                      // FIXED: Use item.id instead of item._id
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className={`cursor-pointer transition-all duration-200 ${
                        selectedItem?.id === item.id
                          ? "bg-teal-50"
                          : "hover:bg-teal-50"
                      }`}
                    >
                      <td className="px-4 py-2 whitespace-nowrap">
                        {item.brand || "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {item.size || "-"}
                      </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                        {item.saleprice || "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {item.uniquesize || "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {item.ampere || "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {item.position || "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {item.origin || "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {item.quantity || "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {item.category?.name || "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {item.reserve_for_branch || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              No items available
            </div>
          )}
        </div>
      )}

      {/* Stock Details Section */}
      {selectedItem && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <h2 className="text-2xl font-semibold mb-4">
            Stock History for {selectedItem.name} ({selectedItem.brand || ""})
          </h2>

          {loadingStockData ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
            </div>
          ) : stockData ? (
            <>
              <div className="flex justify-end mb-4">
                <button
                  onClick={exportToCSV}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition"
                >
                  Export to CSV
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Type</th>
                      <th className="p-2 text-left">Quantity</th>
                      <th className="p-2 text-left">Unit Price</th>
                      <th className="p-2 text-left">Note</th>
                      <th className="p-2 text-left">User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getStockTimeline().map((transaction, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          {new Date(
                            transaction.created_at
                          ).toLocaleDateString()}
                        </td>
                        <td
                          className={`p-2 ${
                            transaction.type === "in"
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {transaction.type === "in" ? "Stock In" : "Stock Out"}
                        </td>
                        <td className="p-2">{transaction.quantity}</td>
                        <td className="p-2">${transaction.unitprice || ""}</td>
                        <td className="p-2">{transaction.note || "-"}</td>
                        <td className="p-2">{transaction.user?.name || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <p className="text-lg font-semibold">
                  Current Quantity Remaining: {stockData.item.quantity}
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-4 text-gray-500">
              No stock data available
            </div>
          )}
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default StockDashboard;